import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import PageHero from "@/components/PageHero";
import Footer from "@/components/Footer";
import CallToAction from "@/components/CallToAction";
import ServiceSection, { ServiceSectionData } from "@/components/ServiceSection";
import ServicesSideNav from "@/components/ServicesSideNav";
import { Target, ShieldCheck, Rocket, HeartHandshake } from "lucide-react";

const services: ServiceSectionData[] = [
  {
    id: "strategy",
    index: 1,
    eyebrowLabel: "Strategy",
    title: "AI Strategy & Vision",
    icon: Target,
    duration: "4-6 weeks",
    complexity: "Strategic",
    intro:
      "AI is reshaping industries, business models, and customer expectations – but for many leaders, the big question remains: What does this mean for us?",
    serviceDescription:
      "Our AI Business Strategy service helps decision-makers and business leaders who want to move beyond the AI hype and develop a clear, actionable understanding of how artificial intelligence can (and will) impact their organization.",
    explorationPoints: [
      "What AI is – and what it isn't",
      "How AI is changing the broader business landscape",
      "What opportunities and challenges AI may bring to your organization",
      "What capabilities you need to build today to stay competitive tomorrow",
    ],
    process:
      "Through executive workshops, strategic assessments, and hands-on collaboration, we help you define your AI ambition, align it with your business goals, and map the path forward.",
    result:
      "The result? A tailored AI strategy that gives you clarity, confidence, and a roadmap for responsible transformation, both short-term wins and long-term value.",
  },
  {
    id: "governance",
    index: 2,
    eyebrowLabel: "Governance",
    title: "AI Governance & Compliance",
    icon: ShieldCheck,
    duration: "6-8 weeks",
    complexity: "Governance",
    intro:
      "As AI becomes integral to business operations, ensuring responsible and compliant AI use is no longer optional – it's essential.",
    serviceDescription:
      "Our AI Governance & Compliance service helps organizations establish clear frameworks for ethical AI development, deployment, and monitoring while ensuring full alignment with emerging regulations like the EU AI Act.",
    explorationPoints: [
      "Understanding EU AI Act requirements and risk classifications",
      "Establishing AI ethics principles and decision-making frameworks",
      "Implementing accountability structures and documentation practices",
      "Building monitoring systems for ongoing compliance and safety",
    ],
    process:
      "We work closely with your leadership, legal, and technical teams to design governance structures that balance innovation with responsibility, creating practical policies that protect your organization while enabling AI growth.",
    result:
      "You'll gain a comprehensive governance framework that ensures compliant, ethical AI use that gives stakeholders confidence and your organization a competitive advantage through trusted AI practices.",
  },
  {
    id: "implementation",
    index: 3,
    eyebrowLabel: "Implementation",
    title: "AI Implementation Leadership",
    icon: Rocket,
    duration: "3-6 months",
    complexity: "Tactical",
    intro:
      "Having an AI strategy is one thing – executing it successfully is another. Many organizations struggle to bridge the gap between vision and reality.",
    serviceDescription:
      "Our AI Implementation Leadership service provides experienced leadership to guide your AI initiatives from concept to deployment, ensuring projects deliver real business value while managing risks and change effectively.",
    explorationPoints: [
      "Designing pilot projects that demonstrate quick wins",
      "Building cross-functional teams with the right mix of skills",
      "Managing stakeholder expectations and organizational change",
      "Scaling successful pilots into enterprise-wide solutions",
    ],
    process:
      "We embed with your teams as interim AI leaders or program managers, bringing proven methodologies, technical expertise, and change management skills to drive momentum and overcome implementation challenges.",
    result:
      "Your AI initiatives move from planning to production with confidence, delivering measurable results while building internal capabilities for sustained AI success.",
  },
  {
    id: "support",
    index: 4,
    eyebrowLabel: "Support",
    title: "Ongoing AI Support & Coaching",
    icon: HeartHandshake,
    duration: "Ongoing",
    complexity: "Dynamic",
    intro:
      "AI transformation is a journey, not a destination. Even after initial implementation, organizations need ongoing support to navigate evolving technologies, challenges, and opportunities.",
    serviceDescription:
      "Our Ongoing AI Support & Coaching service provides continuous advisory support to your leadership and teams, helping you adapt to new developments, optimize existing initiatives, and maintain momentum in your AI transformation.",
    explorationPoints: [
      "Regular strategic check-ins and advisory sessions",
      "Coaching for AI leaders and team members",
      "Guidance on emerging AI technologies and trends",
      "Troubleshooting implementation challenges and roadblocks",
      "Change management and communication",
    ],
    process:
      "Through flexible engagement models – from monthly advisory sessions to on-demand support – we serve as your trusted AI partner, providing expert guidance whenever you need it.",
    result:
      "You'll have a reliable AI advisor by your side, ensuring your organization stays current, overcomes obstacles quickly, and continues to extract maximum value from AI investments.",
  },
];

const navItems = services.map((s) => ({
  id: s.id,
  label: s.title,
  icon: s.icon,
}));

const Services = () => {
  const serviceJsonLd = services.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.title,
    description: s.serviceDescription,
    provider: {
      "@type": "Organization",
      name: "AI-labbet",
      url: "https://www.ailabbet.com/",
    },
    areaServed: "SE",
    url: `https://www.ailabbet.com/services#${s.id}`,
  }));

  return (
    <>
      <SeoHead
        title="Our Services | AI-labbet"
        description="Explore AI-labbet's services: AI strategy consulting, governance frameworks, implementation support, and ongoing coaching."
        path="/services"
        jsonLd={serviceJsonLd}
      />

      <div className="min-h-screen bg-background">
        <Header />
        <PageBreadcrumb currentPage="Services" />

        <main>
          <PageHero
            title={<>Our <span className="text-gradient-brand">Services</span></>}
            description="Comprehensive AI leadership and business guidance tailored to your needs"
            blobPosition="right"
          />

          <section
            id="services-top"
            className="relative py-12 lg:py-20 px-4 sm:px-6 bg-background"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-mesh opacity-25 overflow-hidden"
            />
            <div className="relative container mx-auto max-w-7xl">
              <div className="lg:grid lg:grid-cols-[18rem_1fr] lg:gap-16 lg:items-start">
                <ServicesSideNav items={navItems} variant="sticky" />

                <div>
                  <ServicesSideNav items={navItems} variant="inline" />
                  {services.map((service) => (
                    <ServiceSection key={service.id} service={service} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <CallToAction />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Services;
